import { MIN_SCHEDULE_LEAD_SECONDS } from '@postpilot/core/domain';
import { CalendarIcon, Clock } from 'lucide-react';
import * as React from 'react';
import { DayPicker } from 'react-day-picker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/primitives';
// Not 'dayjs' — this module has the relativeTime plugin registered, without
// which `.from()` below throws at runtime.
import { dayjs } from '@/lib/dayjs';
import { cn } from '@/lib/utils';

/**
 * Date + time picker for scheduling.
 *
 * Times are entered and displayed in the user's LOCAL timezone and converted to
 * UTC only at the boundary (`value` is always an ISO string with an offset).
 * Getting this backwards — showing UTC to the user — is the classic scheduling
 * bug: someone in Sydney schedules a post for "9am" and it goes out at 8pm.
 */
export function SchedulePicker({
  value,
  onChange,
  error,
}: {
  value: string | null;
  onChange: (iso: string | null) => void;
  error?: string;
}) {
  const [open, setOpen] = React.useState(false);

  const selected = value ? dayjs(value) : null;

  // Default the time to the next round half-hour — a sensible starting point
  // that is always safely in the future.
  const [time, setTime] = React.useState(() =>
    (selected ?? dayjs().add(30, 'minute')).format('HH:mm'),
  );

  const commit = (date: Date | undefined, hhmm: string) => {
    if (!date) {
      onChange(null);
      return;
    }

    const [hours, minutes] = hhmm.split(':').map(Number);
    const next = dayjs(date)
      .hour(hours ?? 0)
      .minute(minutes ?? 0)
      .second(0)
      .millisecond(0);

    // `toISOString()` produces UTC with the correct offset applied, which is
    // exactly what the API's Zod schema expects.
    onChange(next.toISOString());
  };

  const isTooSoon =
    selected !== null && selected.diff(dayjs(), 'second') < MIN_SCHEDULE_LEAD_SECONDS;

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">Schedule for</label>

      <div className="flex gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              className={cn(
                'flex-1 justify-start font-normal',
                !selected && 'text-muted-foreground',
              )}
            >
              <CalendarIcon />
              {selected ? selected.format('ddd, D MMM YYYY') : 'Pick a date'}
            </Button>
          </PopoverTrigger>

          <PopoverContent className="w-auto p-3" align="start">
            <DayPicker
              mode="single"
              selected={selected?.toDate()}
              onSelect={(date) => {
                commit(date, time);
                setOpen(false);
              }}
              // Nothing in the past. Disabling it is better than validating after
              // the fact — the user never gets to make the mistake.
              disabled={{ before: new Date() }}
              showOutsideDays
              className="text-sm"
              classNames={{
                months: 'flex flex-col',
                month: 'space-y-3',
                caption: 'flex items-center justify-center relative pt-1',
                caption_label: 'text-sm font-medium',
                nav: 'flex items-center gap-1',
                nav_button: cn(
                  'inline-flex size-7 items-center justify-center rounded-md',
                  'hover:bg-accent opacity-60 transition-opacity hover:opacity-100',
                ),
                nav_button_previous: 'absolute left-1',
                nav_button_next: 'absolute right-1',
                table: 'w-full border-collapse',
                head_row: 'flex',
                head_cell: 'w-8 text-[11px] font-normal text-muted-foreground',
                row: 'flex w-full mt-1',
                cell: 'p-0',
                day: cn(
                  'inline-flex size-8 items-center justify-center rounded-md text-sm transition-colors',
                  'hover:bg-accent focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-2',
                ),
                day_selected:
                  'bg-primary text-primary-foreground hover:bg-primary focus:bg-primary',
                day_today: 'font-semibold text-primary',
                day_outside: 'text-muted-foreground/40',
                day_disabled: 'text-muted-foreground/30 pointer-events-none',
              }}
            />
          </PopoverContent>
        </Popover>

        <div className="relative">
          <Clock className="text-muted-foreground pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2" />
          <Input
            type="time"
            value={time}
            onChange={(event) => {
              setTime(event.target.value);
              if (selected) commit(selected.toDate(), event.target.value);
            }}
            className="w-32 pl-8"
            aria-label="Time"
          />
        </div>
      </div>

      {selected && !isTooSoon && (
        <p className="text-muted-foreground text-xs">
          {/* Relative time is what people actually reason about ("in 2 hours"),
              but the absolute time is what they need to verify. Show both. */}
          Goes out {selected.from(dayjs())} — {selected.format('D MMM [at] HH:mm')}{' '}
          <span className="opacity-60">({Intl.DateTimeFormat().resolvedOptions().timeZone})</span>
        </p>
      )}

      {isTooSoon && (
        <p className="text-destructive text-xs" role="alert">
          Pick a time at least {MIN_SCHEDULE_LEAD_SECONDS / 60} minute from now — the scheduler
          checks for due posts once a minute.
        </p>
      )}

      {error && (
        <p className="text-destructive text-xs" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
